package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.FontStyleAst
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 screen 顶层 `font { name = "..."; bold = ...; italic = ...; shadow = ... }` 块。
 *
 * - 四个字段均可选、各最多出现一次；未知字段报错。
 * - name 为字符串字面量；bold / italic / shadow 为布尔字面量。
 * - 字段间分隔符宽松：换行或分号（`;`）均可，多个连续分隔符忽略。
 *
 * 调用前 ScreenParser 已确认当前 token 是软关键字 `font` 且后随 `{`；本类负责消费 `font` 与整对花括号。
 */
internal class FontBlockParser(private val p: Parser) {

    fun parse(): FontStyleAst {
        // `font` 是软关键字（普通 IDENTIFIER），直接消费
        val fontTok = p.expect(TokenType.IDENTIFIER, "font 块缺少 'font' 关键字")
        p.expect(TokenType.LBRACE, "font 块缺少 '{'")
        skipSeparators()

        var name: String? = null
        var bold: Boolean? = null
        var italic: Boolean? = null
        var shadow: Boolean? = null

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val fieldTok = p.expect(TokenType.IDENTIFIER, "font 字段名缺失")
            val field = fieldTok.lexeme
            p.expect(TokenType.EQUALS, "font 字段 '$field' 后缺少 '='")
            when (field) {
                "name" -> {
                    if (name != null) p.fail("font 字段 'name' 重复声明", fieldTok.position)
                    name = parseStringValue("name")
                }
                "bold" -> {
                    if (bold != null) p.fail("font 字段 'bold' 重复声明", fieldTok.position)
                    bold = parseBoolValue("bold")
                }
                "italic" -> {
                    if (italic != null) p.fail("font 字段 'italic' 重复声明", fieldTok.position)
                    italic = parseBoolValue("italic")
                }
                "shadow" -> {
                    if (shadow != null) p.fail("font 字段 'shadow' 重复声明", fieldTok.position)
                    shadow = parseBoolValue("shadow")
                }
                else -> p.fail(
                    "font 块不支持字段 '$field'（仅 name / bold / italic / shadow）",
                    fieldTok.position,
                )
            }
            skipSeparators()
        }

        val close = p.expect(TokenType.RBRACE, "font 块缺少 '}'")
        return FontStyleAst(
            name = name,
            bold = bold,
            italic = italic,
            shadow = shadow,
            position = p.completePosition(fontTok.position, close),
        )
    }

    /** 跳过连续的字段分隔符：换行或分号。 */
    private fun skipSeparators() {
        while (p.check(TokenType.NEWLINE) || p.check(TokenType.SEMICOLON)) {
            p.advance()
        }
    }

    private fun parseStringValue(field: String): String {
        val t = p.expect(TokenType.STRING, "font 字段 '$field' 必须是字符串字面量")
        return t.literalValue as String
    }

    private fun parseBoolValue(field: String): Boolean {
        val t = p.expect(TokenType.BOOLEAN, "font 字段 '$field' 必须是布尔字面量")
        return t.literalValue as Boolean
    }
}
